import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Matrix } from "@babylonjs/core/Maths/math.vector";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import type { Scene } from "@babylonjs/core/scene";
import "@babylonjs/core/Meshes/thinInstanceMesh";

/**
 * Cheering in a stadium (ADR-0044 decision 4), as the browser shows it.
 *
 * Founder feedback 2026-10-26 ("When I click 'Cheer for' this weird colored rectangle appears"): the cheer used to put a
 * bare plane in the team's colour 3.5 m over the cheering fan's seat. From the walk camera behind a seated fan it hung
 * over the field (at midfield, from a seat on the 50) as a stray dark rectangle, in all five stadiums. Now a cheering
 * fan waves a small team pennant on a white pole from the right hand, the pole angled out to the side (so the pennant
 * flies clear of the fan's name tag) and never higher than CHEER_FLAG_TOP over the seat; they hop in their seat; and
 * the drawn crowd around them jumps up in a ripple that runs out from them (crowdRippleLift), whatever the crowd noise.
 */

/** Cheers are shown for this long (the server keeps them in the state as long). */
export const CHEER_SHOW_MS = 1600;

export const CHEER_FLAG = {
  /** The hand holding the pole: to the fan's right, a little forward, this high over the seat's tread. */
  hand: { right: 0.34, forward: 0.12, up: 1.0 },
  /** Pole length, how far it leans out to the fan's right (radians), and the pennant (a triangle at the pole's top). */
  pole: 1.1,
  lean: 0.62,
  pennant: { length: 0.72, height: 0.4 },
} as const;
/** How high a cheering fan hops in their seat (metres). */
export const CHEER_HOP = 0.32;
/** The highest a cheer flag reaches over the seat's tread (hop and sway included). */
export const CHEER_FLAG_TOP = CHEER_FLAG.hand.up + CHEER_HOP + CHEER_FLAG.pole * Math.cos(CHEER_FLAG.lean - 0.25) + 0.05;

/** The crowd's ripple around a cheering fan: how far it reaches, how long each figure jumps, how fast it spreads. */
export const CROWD_RIPPLE = { radius: 8, ms: 700, delayPerMetre: 70, height: 0.42 } as const;

/** The hop of a cheering fan `ageMs` into the cheer (0 once it is over). */
export function cheerHop(ageMs: number): number {
  return ageMs >= 0 && ageMs < CHEER_SHOW_MS ? Math.abs(Math.sin((ageMs / 1000) * 9)) * CHEER_HOP : 0;
}

/** How high a drawn fan `metres` from a cheering fan jumps `ageMs` into the cheer (0 outside the ripple). */
export function crowdRippleLift(metres: number, ageMs: number): number {
  if (metres > CROWD_RIPPLE.radius) return 0;
  const a = ageMs - metres * CROWD_RIPPLE.delayPerMetre;
  if (a <= 0 || a >= CROWD_RIPPLE.ms) return 0;
  return Math.sin((Math.PI * a) / CROWD_RIPPLE.ms) * CROWD_RIPPLE.height * (1 - metres / (CROWD_RIPPLE.radius + 1));
}

export interface CheerSpot {
  x: number;
  /** The seat's tread (the floor under a standing fan). */
  y: number;
  z: number;
  facing: number;
}

/** The pole's foot (in the fan's hand), its lean (out to the right, swaying) and the pennant's flap, `ageMs` into the cheer. */
export function cheerFlagPose(at: CheerSpot, ageMs: number, i: number): { x: number; y: number; z: number; yaw: number; sway: number; flap: number } {
  const t = ageMs / 1000;
  const fx = Math.sin(at.facing);
  const fz = Math.cos(at.facing);
  // Right of the facing direction: (cos, -sin).
  const rx = Math.cos(at.facing);
  const rz = -Math.sin(at.facing);
  const h = CHEER_FLAG.hand;
  return {
    x: at.x + rx * h.right + fx * h.forward,
    y: at.y + h.up + cheerHop(ageMs),
    z: at.z + rz * h.right + fz * h.forward,
    yaw: at.facing,
    sway: -(CHEER_FLAG.lean + Math.sin(t * 9 + i) * 0.25),
    flap: Math.sin(t * 7 + i * 1.7) * 0.55,
  };
}

export interface CheerFlagsHandle {
  meshes: Mesh[];
  /** Draw a flag for each cheer: where (the seat or where the fan stands), the team colour, and the cheer's age. */
  update(flags: ReadonlyArray<{ at: CheerSpot; colour: string; ageMs: number }>): void;
  count(): number;
  dispose(): void;
}

/** The cheer flags: one thin-instanced pole and one thin-instanced pennant for every cheering fan (two draw calls). */
export function buildCheerFlags(scene: Scene): CheerFlagsHandle {
  const poleMat = new StandardMaterial("arenaCheerPoleMat", scene);
  poleMat.diffuseColor = new Color3(0.94, 0.94, 0.92);
  poleMat.specularColor = new Color3(0.1, 0.1, 0.1);
  const pole = CreateBox("arenaCheerPole", { width: 0.035, height: CHEER_FLAG.pole, depth: 0.035 }, scene);
  pole.bakeTransformIntoVertices(Matrix.Translation(0, CHEER_FLAG.pole / 2, 0));
  pole.material = poleMat;

  const pennantMat = new StandardMaterial("arenaCheerPennantMat", scene);
  pennantMat.diffuseColor = Color3.White();
  pennantMat.emissiveColor = new Color3(0.22, 0.22, 0.22);
  pennantMat.specularColor = Color3.Black();
  pennantMat.backFaceCulling = false;
  // A triangle hanging from the pole's top, flying out to the fan's right (x).
  const pennant = new Mesh("arenaCheerPennant", scene);
  const top = CHEER_FLAG.pole;
  const { length, height } = CHEER_FLAG.pennant;
  const vd = new VertexData();
  vd.positions = [0, top, 0, 0, top - height, 0, length, top - height / 2, 0];
  vd.indices = [0, 1, 2];
  vd.normals = [0, 0, -1, 0, 0, -1, 0, 0, -1];
  vd.applyToMesh(pennant);
  pennant.material = pennantMat;

  for (const m of [pole, pennant]) {
    m.isPickable = false;
    m.alwaysSelectAsActiveMesh = true;
    m.setEnabled(false);
  }
  let count = 0;
  const yawRoll = new Matrix();
  const flapM = new Matrix();
  const both = new Matrix();

  return {
    meshes: [pole, pennant],
    update(flags) {
      const n = flags.length;
      if (n === 0 && count === 0) return;
      const poles = new Float32Array(Math.max(1, n) * 16);
      const pennants = new Float32Array(Math.max(1, n) * 16);
      const poleColours = new Float32Array(Math.max(1, n) * 4);
      const colours = new Float32Array(Math.max(1, n) * 4);
      flags.forEach((f, i) => {
        const p = cheerFlagPose(f.at, f.ageMs, i);
        Matrix.RotationYawPitchRollToRef(p.yaw, 0, p.sway, yawRoll);
        yawRoll.setTranslationFromFloats(p.x, p.y, p.z);
        yawRoll.copyToArray(poles, i * 16);
        Matrix.RotationYToRef(p.flap, flapM);
        flapM.multiplyToRef(yawRoll, both);
        both.copyToArray(pennants, i * 16);
        const c = Color3.FromHexString(f.colour);
        colours.set([c.r, c.g, c.b, 1], i * 4);
        poleColours.set([1, 1, 1, 1], i * 4);
      });
      pole.thinInstanceSetBuffer("matrix", poles, 16, false);
      pole.thinInstanceSetBuffer("color", poleColours, 4, false);
      pennant.thinInstanceSetBuffer("matrix", pennants, 16, false);
      pennant.thinInstanceSetBuffer("color", colours, 4, false);
      count = n;
      pole.setEnabled(n > 0);
      pennant.setEnabled(n > 0);
    },
    count: () => count,
    dispose() {
      pole.dispose();
      pennant.dispose();
      poleMat.dispose();
      pennantMat.dispose();
    },
  };
}
